/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/prop-types */
import React, { FC, useEffect, useState } from 'react';
import {
    SelectionMode,
    DetailsListLayoutMode,
    IOverflowSetItemProps,
    CommandBarButton,
    OverflowSet
} from '@fluentui/react';
import {
    DetailsList,
    ColumnInfo,
    FilteredColumn,
    SortedColumnInfo,
    FilterType,
    Language,
} from '@eta/components';
import { useTranslation } from 'react-i18next';
import './IntermediariesGrid.scss';
import { TaxtpayerIntermediaryDto, IntermediaryStatuses, MetaData } from '../Service/proxy';
import { UIHelpers } from '../../helpers/UIHelpers';
import { Defaults } from '../../common/config/constants';
import { ServiceCreator } from '../Service/client';
import ManageIntermediary from './ManageIntermediary';

interface IntermediariesGridProps {
    items: TaxtpayerIntermediaryDto[] | undefined;
    currentLanguage: Language;
}

export const IntermediariesGrid: FC<IntermediariesGridProps> = (props: IntermediariesGridProps) => {

    const { t } = useTranslation(['intermediaries', 'common']);
    const [columns, setColumns] = useState<ColumnInfo[]>();
    const {items: itemsProps, currentLanguage: currentLanguageProps } = props;

    const [items, setItems] = useState<TaxtpayerIntermediaryDto[]>();

    const [filters, setFilters] = useState<FilteredColumn[]>([]);
    const [pageSize, setPageSize] = useState<number>(Defaults.PageSize);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [metadata, setMetadata] = useState<MetaData | undefined>();
    const [currentLanguage, setCurrentLanguage] = useState<Language>(
        currentLanguageProps
    );
    const [showIntermediaryDialog, setShowIntermediaryDialog] = useState<boolean>(false);
    const [RepresentationId, setRepresentationId] = useState<number>(0);

    const onRenderItemStyles = {
        root: { padding: '10px' },
        menuIcon: { fontSize: '16px' },
    };
    const onRenderOverflowButtonStyles = {
        root: { padding: '10px' },
        menuIcon: { fontSize: '16px' },
    };
    const onRenderItem = ({ name, icon, onClick }: IOverflowSetItemProps): JSX.Element => {
        return (
            <CommandBarButton
                role="menuitem"
                aria-label={name}
                styles={onRenderItemStyles}
                iconProps={{ iconName: icon }}
                onClick={onClick}
            />
        );
    };
    const onRenderOverflowButton = (overflowItems?: any[]): JSX.Element => {
        return (
            <CommandBarButton
                role="menuitem"
                title="More items"
                styles={onRenderOverflowButtonStyles}
                menuIconProps={{ iconName: 'MoreVertical' }}
                menuProps={{ items: overflowItems ?? [] }}
            />
        );
    };
    const onEdit=async (data: TaxtpayerIntermediaryDto)=>{
        setShowIntermediaryDialog(true);
        setRepresentationId(data.id??0);
     };
    useEffect(() => {
        setItems(itemsProps?.slice());
    }, [itemsProps]);

    useEffect(() => {
        if (currentLanguage.lang !== currentLanguageProps.lang) {
            setCurrentLanguage(currentLanguageProps);
        }
    }, [currentLanguageProps, items, currentLanguage]);

    const loadTaxpayerIntermediaries = async (pageNumber?: number, ps?: number, name?: string, nameAr?: string) => {
        try {
            const client = ServiceCreator.getProviderManagementPublicClient();
            const response = await client.getTaxpayerIntermediaries(name, nameAr, ps, pageNumber);
            setItems(response.result);
            setMetadata(response.metadata);
        // eslint-disable-next-line no-empty
        } catch (ex) {
        }
    };
    
    const filterTaxpayers = (pageNo: number, nbItemPerPage: number, appliedFilters: FilteredColumn[]) => {
        const name = appliedFilters && appliedFilters.find((i: FilteredColumn) => i.criteria[0] && i.criteria[0].fieldName === 'namePrimaryLang');
        const nameArabic = appliedFilters && appliedFilters.find((i: FilteredColumn) => i.criteria[0] && i.criteria[0].fieldName === 'nameSecondaryLang');

        const nameEn = name && name.criteria?.[0]?.filterValue?.toString();
        const nameAr = nameArabic && nameArabic.criteria?.[0]?.filterValue?.toString();

        loadTaxpayerIntermediaries(pageNo, nbItemPerPage, nameEn, nameAr);
    };

    const onNbItemPerPageChanged = (nbItemPerPage: number) => {
        setPageSize(nbItemPerPage);
        filterTaxpayers(1, nbItemPerPage, filters);
        return true;
    };

    const onPageChanged = (pageNum: number) => {
        setCurrentPage(pageNum + 1);
        return true;
    };

    const onGridChanged = (pageIndex: number, filteredColumns: FilteredColumn[]) => {
        setFilters(filteredColumns);

        filterTaxpayers(pageIndex + 1, pageSize, filteredColumns);
        return false;
    };

    useEffect(() => {
        const gridColumns = [
            {
                key: 'namePrimaryLang',
                name: t('LinkedTaxpayer.Name'),
                fieldName: 'namePrimaryLang',
                minWidth: 170,
                maxWidth: 170,
                isResizable: true,
                isSortedDescending: true,
                filterOption: {
                    placeholder: '',
                    type: FilterType.Text,
                    fieldNames: [
                        {
                            fieldName: 'namePrimaryLang',
                            displayName: t('LinkedTaxpayer.Name'),
                        }
                    ]
                },
                onRender: (item: TaxtpayerIntermediaryDto) => {
                    return (
                        <span>{item.namePrimaryLang}</span>
                    );
                },
            },
            {
                key: 'nameSecondaryLang',
                name: t('LinkedTaxpayer.Name[AR]'),
                fieldName: 'nameSecondaryLang',
                minWidth: 170,
                maxWidth: 170,
                isResizable: true,
                isSortedDescending: false,
                filterOption: {
                    placeholder: '',
                    type: FilterType.Text,
                    fieldNames: [
                        {
                            fieldName: 'nameSecondaryLang',
                            displayName: t('LinkedTaxpayer.Name[AR]'),
                        }
                    ]
                },
                onRender: (item: TaxtpayerIntermediaryDto) => {
                    return (
                        <span>{item.nameSecondaryLang}</span>
                    );
                },
            },
            {
                key: 'url',
                name: t('LinkedTaxpayer.URL'),
                fieldName: 'url',
                minWidth: 150,
                maxWidth: 150,
                isResizable: true,
                isSortedDescending: false,
                onRender: (item: TaxtpayerIntermediaryDto) => {
                    return (
                        <a href={item.url} target='_blank' rel="noopener noreferrer">{item.url}</a>
                    );
                },
            },
            {
                key: 'activeFromTo',
                name: t('LinkedTaxpayer.RepresentationFromTo'),
                fieldName: 'activeFromTo',
                minWidth: 130,
                maxWidth: 130,
                isResizable: true,
                isSortedDescending: false,
                onRender: (item: TaxtpayerIntermediaryDto) => {
                    return (
                        <>
                          <span>{UIHelpers.getLocalizedDateTime('Date', item.representationFrom, currentLanguageProps.lang)}</span>
                           -
                          <span>{UIHelpers.getLocalizedDateTime('Date', item.representationTo, currentLanguageProps.lang)}</span>

                       </>
                    );
                },
            },
            {
                key: 'isActive',
                name: t('LinkedTaxpayer.IntermediaryStatus'),
                fieldName: 'isActive',
                minWidth: 150,
                maxWidth: 150,
                isRowHeader: true,
                isResizable: false,
                onRender: (item: TaxtpayerIntermediaryDto) => {
                    return (
                        <div className="horizontal">
                        <span className={item.status === IntermediaryStatuses.Unblocked ? 'green' : 'red'}>
                            {item.status === IntermediaryStatuses.Unblocked
                                ? t('LinkedTaxpayer.status-allowed')
                                : t('LinkedTaxpayer.status-notAllowed')}
                        </span>
                    </div>

                    );
                },
            },
            {
                key: 'action',
                name: '',
                minWidth: 40,
                maxWidth: 40,
                isResizable: false,
                data: 'string',
                isPadded: true,
                onRender: (data: TaxtpayerIntermediaryDto) => {
                    return (
                    
                                    <div>
                                        <OverflowSet
                                            role="menubar"
                                            vertical
                                            overflowItems={[
                                                {
                                                    key: 'editButton',
                                                    iconProps: {
                                                        iconName: 'Edit',
                                                    },
                                                    name: t('editPermession'),
                                                    onClick: () => {
                                                        onEdit(data);
                                                    },
                                                },
                                                
                                            ]}
                                            onRenderOverflowButton={
                                                onRenderOverflowButton
                                            }
                                            onRenderItem={onRenderItem}
                                        />
                                    </div>
                      
                            );
                },
            },
        ];
        setColumns(gridColumns);
    }, [t]);

    return (
        <>
        <DetailsList
            columns={columns ?? []}
            items={items}
            selectionMode={SelectionMode.none}
            layoutMode={DetailsListLayoutMode.justified}
            noItemsPlaceholder={<span>{t('common:NoRecords')} </span>}
            labels={{ resultPerPage: t('common:nbItemPerPage'), totalRecord: t('common:totalRecord') }}
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onChanged={(pageIndex: number, filteredColumn: FilteredColumn[], sortedColumn?: SortedColumnInfo) => 
                onGridChanged(pageIndex, filteredColumn)}
            paginationSetting={{
                nbItemPerPage: pageSize,
                nbPageShown: 5,
                currentPage: currentPage - 1,
                totalPage: (metadata && metadata.totalPages) || 0,
            }}
            onPageChanged={(pageIndex: number) => onPageChanged(pageIndex)}
            totalItemCount={(metadata && metadata.totalCount) || 0}
            onNbItemPerPageChanged={onNbItemPerPageChanged}
        />
        {showIntermediaryDialog && (
            <ManageIntermediary
                hidden={!showIntermediaryDialog}
                onClose={() => setShowIntermediaryDialog(false)}
                onSave={() => {setShowIntermediaryDialog(false);onGridChanged(currentPage-1, filters );}}
                RepresentationId={RepresentationId}
                isEdit

                 />
        )}
        </>
    );
};
